/** A standalone first watch, reached directly from the main menu. */
import { destination, NM_TO_M } from '@atc/core';
import { parseCommand } from '@atc/sim';
import type { Lesson, StepContext, StepResult } from './lesson';
import { everSawFor, runway, spoken } from './lesson-helpers';

const ARRIVAL = 'ELY312';
const DEPARTURE = 'ISR204';

const arrivalLost = (ctx: StepContext): StepResult => {
  if (everSawFor(ctx, 'goAround', ARRIVAL)) return { fail: `${ARRIVAL} went around. Restart, use its card to clear the ILS approach and then clear it to land.` };
  if (everSawFor(ctx, 'missedIntercept', ARRIVAL)) return { fail: `${ARRIVAL} missed the final. Restart and issue the landing clearance sooner.` };
  return null;
};
const usedTakeoffCard = (ctx: StepContext) => ctx.cardCommands?.some((text) => {
  const parsed = parseCommand(text);
  return parsed.ok && parsed.command.type === 'aircraft' && parsed.command.callsign === DEPARTURE && parsed.command.instructions.some((instruction) => instruction.kind === 'takeoff');
});

export const TUTORIAL: Lesson = {
  id: 'tutorial',
  title: 'Your first watch',
  summary: 'Land one arrival, then launch a departure from the same runway.',
  airport: 'llbg',
  tier: 'beginner',
  realism: 'low',
  realismOverrides: { wind: 'none', complianceDelayS: [0, 0], wrongReadbackRate: 0, runwayOccupancyCheck: true, pilotRequests: false },
  runways: { arrivals: ['12'], departures: ['12'] },
  commands: ['approach', 'land', 'lineUp', 'takeoff'],
  briefing: {
    situation: 'Ben Gurion · runway 12 for arrivals and departures',
    scene: [
      `${ARRIVAL} is inbound on the extended centreline for runway 12. ${DEPARTURE} is holding short of that same runway.`,
      'You control both flights. Clear the arrival for the ILS approach, then clear it to land. Wait for it to leave the runway before launching the departure. The radio log shows each call and readback.',
    ],
    objectives: [`Clear ${ARRIVAL} for the ILS approach and landing`, 'Wait until runway 12 is clear', `Line up and launch ${DEPARTURE}`],
    notes: ['The coach pauses traffic at each decision.', 'Use the buttons in the aircraft cards to learn the controls. You can use voice or type commands in later watches.'],
    chartIds: ['approach-ils-rwy-12'],
  },
  build(airport) {
    const rw = runway(airport, '12');
    const arriving = spoken(airport, ARRIVAL);
    const departing = spoken(airport, DEPARTURE);
    return {
      setup(sim) {
        const p = destination(rw.lat, rw.lon, (rw.headingDeg + 180) % 360, 12 * NM_TO_M);
        sim.addAircraft({ kind: 'arrival', callsign: ARRIVAL, type: 'B738', ...p, altitudeFt: 3000, headingDeg: rw.headingDeg, iasKt: 180, runway: '12' });
        sim.addAircraft({ kind: 'departure', callsign: DEPARTURE, type: 'A320', runway: '12' });
        // Emit the opening picture without making any controller transmission for the player.
        sim.step(0.25);
      },
      steps: [
        {
          title: 'Select the arrival',
          text: `${ARRIVAL} is the aircraft approaching runway 12. Click its radar blip or cream flight strip. The card shows its callsign, flight status and available commands. Traffic waits while you look.`,
          hint: `Click ${ARRIVAL} on the radar or in the strip list.`,
          say: `Select ${ARRIVAL}; no radio call yet.`,
          solution: [],
          check: (ctx) => ctx.selections?.includes(ARRIVAL) ? 'pass' : null,
        },
        {
          title: 'Clear the ILS approach',
          text: `${ARRIVAL} is inbound but has no approach clearance. Click ILS 12 in its card. Listen for the readback, then watch it capture the centreline and report established on final.`,
          hint: `Click ILS 12 in ${ARRIVAL}'s card.`,
          say: `${arriving}, cleared ILS approach runway one two.`,
          solution: [`${ARRIVAL} I 12`],
          check: (ctx) => arrivalLost(ctx) ?? (ctx.cardCommands?.includes(`${ARRIVAL} I 12`) && ctx.sim.find(ARRIVAL)?.approach?.established ? 'pass' : null),
          testTimeoutS: 120,
        },
        {
          title: 'Clear it to land',
          text: `${ARRIVAL} is established on final. An ILS approach clearance is not permission to land. Click Land 12 in its card and watch the separate landing readback in the radio log.`,
          hint: `Click Land 12 in ${ARRIVAL}'s card.`,
          say: `${arriving}, runway one two, cleared to land.`,
          solution: [`${ARRIVAL} L 12`],
          check: (ctx) => arrivalLost(ctx) ?? (ctx.cardCommands?.includes(`${ARRIVAL} L 12`) && ctx.sim.find(ARRIVAL)?.approach?.cleared ? 'pass' : null),
        },
        {
          title: 'Watch the runway',
          text: `Watch ${ARRIVAL} touch down and leave runway 12. ${DEPARTURE} must remain at the holding point until the runway is clear. The radio log reports when the arrival has cleared it.`,
          hint: 'Watch the arrival and wait for its clear-of-runway call.',
          say: 'No radio call is needed while the arrival clears the runway.',
          solution: [],
          check: (ctx) => arrivalLost(ctx) ?? (everSawFor(ctx, 'clearOfRunway', ARRIVAL) ? 'pass' : null),
          testTimeoutS: 900,
        },
        {
          title: 'Select the departure',
          text: `Runway 12 is clear. Click ${DEPARTURE}, the blue flight strip or its radar target, to see the commands for an aircraft holding short.`,
          hint: `Click ${DEPARTURE}.`,
          say: `Select ${DEPARTURE}; no radio call yet.`,
          solution: [],
          check: (ctx) => ctx.selections?.includes(DEPARTURE) ? 'pass' : null,
        },
        {
          title: 'Line up',
          text: `Click Line up in ${DEPARTURE}'s card. This lets the aircraft enter the runway and wait; it is not yet cleared for takeoff.`,
          hint: `Click Line up in ${DEPARTURE}'s card.`,
          say: `${departing}, runway one two, line up and wait.`,
          solution: [`${DEPARTURE} W`],
          check: (ctx) => ctx.cardCommands?.includes(`${DEPARTURE} W`) && ctx.sim.find(DEPARTURE)?.phase === 'linedUp' ? 'pass' : null,
          testTimeoutS: 120,
        },
        {
          title: 'Clear for takeoff',
          text: `The runway is yours. Click the takeoff command in ${DEPARTURE}'s card. The card may pair takeoff with a climb clearance; watch the readback, roll and liftoff.`,
          hint: `Click the takeoff command in ${DEPARTURE}'s card.`,
          say: `${departing}, runway one two, cleared for takeoff.`,
          solution: [`${DEPARTURE} C 7 T`],
          check: (ctx) => usedTakeoffCard(ctx) && everSawFor(ctx, 'airborne', DEPARTURE) ? 'pass' : null,
          testTimeoutS: 120,
        },
      ],
    };
  },
};
